import addOrUpdateRowData from './table-updates/addOrUpdateRowData';
import removeRowData from './table-updates/removeRowData';
import { action } from 'satcheljs/lib/legacy';
import {
    getRowKeyFromListViewType,
    MailRowDataPropertyGetter,
    MailListRowDataType,
    TableView,
} from 'owa-mail-list-store';
import { trace } from 'owa-trace';

/**
 * Table operations is responsible for 1) Add, 2) Update 3) Remove of
 * the rows from tableView and the list view conversations/items store.
 */

/**
 * Adds row in the listView store
 * @param addAtIndex index where the row needs to be moved
 * @param row to be added
 * @param tableView where row is getting added
 */
export let addRow = action('tableOperations.addRow')(function addRow(
    addAtIndex: number,
    row: MailListRowDataType,
    tableView: TableView
) {
    // The order of operations is important as we have to add the row data that will add the associated model (conv/item) to the store
    // and then add the references for the rowKey and rowId as addToTableRowKeys operates on a rowKey
    // 1. Add or update row in the store
    addOrUpdateRowData(row, tableView);

    // 2. Add to table's rowKeys list
    addToTableRowKeys(
        tableView,
        addAtIndex,
        getRowKeyFromListViewType(row, tableView.tableQuery.listViewType)
    );
});

/**
 * Appends row to the end of the list in TableView
 * @param row to be added
 * @param tableView where row is getting added
 */
export let appendRow = action('tableOperations.appendRow')(function appendRow(
    row: MailListRowDataType,
    tableView: TableView
) {
    // Append to the end of table's rowKeys list
    const appendIndex = tableView.rowKeys.length;
    addRow(appendIndex, row, tableView);
});

/**
 * Removes row in listView store
 * @param rowKey that needs to be removed
 * @param tableView where row is getting removed
 */
export let removeRow = action('tableOperations.removeRow')(function removeRow(
    rowKey: string,
    tableView: TableView
) {
    // The order of operations is important as we have to remove the references for the rowKey and rowId first
    // and then remove row data which also takes care of removing the model data (conv/item) from the store
    // 1. Remove rowKey from rowKeys list
    removeFromTableRowKeys(tableView, rowKey);

    // 2. Remove row from list view store
    removeRowData(rowKey, tableView);
});

/**
 * Updates row index position in the table in list view store.
 * @param updateIndex index where the row needs to be moved
 * @param rowKey rowKey of the row to be updated
 * @param tableView where row is getting updated
 */
export let updateRowPosition = action('tableOperations.updateRowPosition')(
    function updateRowPosition(targetIndex: number, rowKey: string, tableView: TableView) {
        const currentIndex = tableView.rowKeys.indexOf(rowKey);
        if (currentIndex < 0) {
            trace.error('Row not found in table');
            return;
        }

        if (currentIndex == targetIndex) {
            // No updates needed
            return;
        }

        // Remove from table's rowKeys list
        removeFromTableRowKeys(tableView, rowKey);

        // Add to table's rowKeys list
        addToTableRowKeys(tableView, targetIndex, rowKey);
    }
);

/**
 * Updates row's position and data in list view store.
 * @param targetIndex index where the row needs to be moved
 * @param row to be updated
 * @param tableView where row is getting updated
 */
export let updateRow = action('tableOperations.updateRow')(function updateRow(
    targetIndex: number,
    row: MailListRowDataType,
    tableView: TableView
) {
    // Update row's position index in table
    updateRowPosition(
        targetIndex,
        getRowKeyFromListViewType(row, tableView.tableQuery.listViewType),
        tableView
    );

    // Also update row data
    addOrUpdateRowData(row, tableView);
});

/**
 * Updates row data in list view store.
 * @param row to be updated
 * @param tableView where row is getting updated
 */
export let updateRowData = action('tableOperations.updateRowData')(function updateRowData(
    row: MailListRowDataType,
    tableView: TableView
) {
    // Update row data
    addOrUpdateRowData(row, tableView);
});

/**
 * Clears the table of all rows
 * @param tableView to clear
 * @param skipRowsNewerThanTime - timestamp which is used for skipping deleting new emails
 * @param rowKeysToExclude list of rows to exclude from delete operation
 */
export let clear = action('tableOperations.clear')(function clear(
    tableView: TableView,
    skipRowsNewerThanTime: Date = null,
    rowKeysToExclude: string[] = []
) {
    for (let i = tableView.rowKeys.length - 1; i >= 0; i--) {
        const rowKey = tableView.rowKeys[i];

        // Remove all conversation data if no timeStamp is specified and if it is not in exclusion list.
        // Otherwise skip the rows that are newer than the timeStamp
        if (rowKeysToExclude.indexOf(rowKey) == -1) {
            if (!skipRowsNewerThanTime) {
                removeRow(rowKey, tableView);
            } else {
                const lastDeliveryTime = MailRowDataPropertyGetter.getLastDeliveryTimeStamp(
                    rowKey,
                    tableView
                );
                if (new Date(lastDeliveryTime) < skipRowsNewerThanTime) {
                    removeRow(rowKey, tableView);
                }
            }
        }
    }
});

/**
 * Returns flag indicating whether the given table already contains the row with the given key
 * @param tableView - Given tableView
 * @param rowKey - Given row key
 */
export function containsRowKey(tableView: TableView, rowKey: string): boolean {
    return tableView.rowKeys.indexOf(rowKey) >= 0;
}

/**
 * Add the given row Key to the given TableView's rowKeys list
 * @param tableView - The table to act on
 * @param addAtIndex - Index to add the rowKey at
 * @param rowKey - The rowKey to add
 */
function addToTableRowKeys(tableView: TableView, addAtIndex: number, rowKey: string) {
    if (addAtIndex < 0 || addAtIndex > tableView.rowKeys.length) {
        trace.error(
            'Invalid add index ' + addAtIndex + ' with table length ' + tableView.rowKeys.length
        );
        return;
    }

    if (containsRowKey(tableView, rowKey)) {
        trace.error('addToTableRowKeys: Cannot add rowKey to table when it already exists');
        return;
    }

    const rowId = MailRowDataPropertyGetter.getRowIdString(rowKey, tableView);
    let rowKeys: string[] = tableView.rowIdToRowKeyMap.get(rowId);
    if (!rowKeys) {
        rowKeys = [];
    }

    if (rowKeys.indexOf(rowKey) == -1) {
        rowKeys.push(rowKey);
    }

    // Add rowKey to the rowIdToRowKeyMap
    tableView.rowIdToRowKeyMap.set(rowId, rowKeys);

    // Add rowKey to the rowKeys at the specified index
    tableView.rowKeys.splice(addAtIndex, 0, rowKey);
}

/**
 * Remove the given rowKey from the given TableView's rowKeys list
 * @param tableView - The table to act on
 * @param rowKey - The rows key
 */
function removeFromTableRowKeys(tableView: TableView, rowKey: string) {
    const removeAtIndex = tableView.rowKeys.indexOf(rowKey);
    if (removeAtIndex < 0) {
        trace.error('removeFromTableRowKeys: RowKey not found in tableView.rowKeys');
        return;
    }

    const rowId = MailRowDataPropertyGetter.getRowIdString(rowKey, tableView);

    // 1. Remove the rowKey from the rowIdToRowKeyMap and remove the map entry if this is the last rowKey getting removed
    const rowKeysForRowId = tableView.rowIdToRowKeyMap.get(rowId);
    if (!rowKeysForRowId) {
        trace.error('removeFromTableRowKeys: invalid rowKeysForRowId');
        return;
    }

    const indexOfRowKey = rowKeysForRowId.indexOf(rowKey);
    if (indexOfRowKey == -1) {
        trace.error('removeFromTableRowKeys: RowKey should be present in rowIdToRowKey map');
        return;
    }

    if (rowKeysForRowId.length == 1) {
        // Remove the entry for this rowId from the rowIdToRowKeyMap
        tableView.rowIdToRowKeyMap.delete(rowId);
    } else {
        rowKeysForRowId.splice(indexOfRowKey, 1);
    }

    // 2. Remove row at the specified index from row keys
    tableView.rowKeys.splice(removeAtIndex, 1);
}
